import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';

interface RegistrationStepProgressProps {
  steps: ReadonlyArray<string>;
  currentStep: number;
  colorTheme?: 'primary' | 'secondary';
  hasStepError?: (step: number) => boolean;
}

const RegistrationStepProgress: React.FC<RegistrationStepProgressProps> = ({
  steps,
  currentStep,
  colorTheme = 'primary',
  hasStepError,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);
  const activeColor = colorTheme === 'secondary' ? 'bg-secondary ring-secondary' : 'bg-primary ring-primary';
  const completedColor = colorTheme === 'secondary' ? 'bg-secondary' : 'bg-primary';

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const checkOverflow = () => {
      setShowLeftGradient(element.scrollLeft > 0);
      setShowRightGradient(element.scrollLeft < element.scrollWidth - element.clientWidth - 1);
    };
    checkOverflow();
    element.addEventListener('scroll', checkOverflow, { passive: true });
    window.addEventListener('resize', checkOverflow);
    return () => {
      element.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [steps.length]);

  return (
    <div className="relative mb-8" aria-label="Registration progress">
      {showLeftGradient && <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-8 bg-gradient-to-r from-white to-transparent" />}
      {showRightGradient && <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-8 bg-gradient-to-l from-white to-transparent" />}
      <div ref={scrollRef} className="scrollbar-hide mb-2 overflow-x-auto px-1 py-1">
        <div className="flex min-w-max items-center gap-2">
          {steps.map((_, index) => {
            const stepHasError = Boolean(hasStepError?.(index));
            return (
              <div key={index} className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}>
                <div className="flex flex-1 items-center">
                  <div
                    aria-current={index === currentStep ? 'step' : undefined}
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full font-semibold transition-all ${
                      index < currentStep
                        ? `${completedColor} text-white`
                        : index === currentStep
                          ? `${activeColor} text-white ring-2 ring-offset-2`
                          : stepHasError
                            ? 'border-2 border-red-300 bg-white text-red-500'
                            : 'border-2 border-gray-300 bg-white text-gray-400'
                    }`}
                  >
                    {index < currentStep ? <Check className="h-4 w-4" /> : index + 1}
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`mx-2 h-0.5 min-w-8 flex-1 transition-colors ${index < currentStep ? completedColor : 'bg-gray-200'}`} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="text-sm font-medium text-gray-600">
        Step {currentStep + 1} of {steps.length} — {steps[currentStep]}
      </div>
    </div>
  );
};

export default RegistrationStepProgress;
